import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Paperclip, UploadSimple, ArrowsClockwise, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CsvExport } from "@/components/CsvExport";
import { Attachments } from "@/components/Attachments";
import { ExpenseUploadSheet } from "@/pages/expenses/ExpenseUploadSheet";

const CATEGORIES = ["labor", "travel", "utilities", "office", "equipment", "misc"];

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({
    project_id: "", category: "labor", amount: 0,
    date: new Date().toISOString().slice(0, 10), vendor: "", description: "",
  });

  const load = useCallback(
    () => Promise.all([api.get("/expenses"), api.get("/projects")]).then(([a, b]) => { setRows(a.data); setProjects(b.data); }),
    []
  );
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post("/expenses", { ...form, project_id: form.project_id || null, amount: Number(form.amount) });
      toast.success("Expense added");
      setOpen(false);
      setForm({ project_id: "", category: "labor", amount: 0, date: new Date().toISOString().slice(0, 10), vendor: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  const [uploadOpen, setUploadOpen] = useState(false);

  const ribbon = (
    <>
      <BcRibbonGroup label="New">
        <BcRibbonBtn icon={UploadSimple} label="Upload Slip" onClick={() => setUploadOpen(true)} testid="ribbon-upload-slip" />
        <BcRibbonBtn icon={Plus} label="New Expense" variant="primary" onClick={() => setOpen(true)} testid="ribbon-new-expense" />
      </BcRibbonGroup>
      <BcRibbonGroup label="Process">
        <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="ribbon-refresh" />
      </BcRibbonGroup>
      <BcRibbonGroup label="Report">
        <CsvExport resource="expenses" render={({ onClick }) => (
          <BcRibbonBtn icon={FileArrowDown} label="Export CSV" onClick={onClick} testid="ribbon-export-csv" />
        )} />
      </BcRibbonGroup>
    </>
  );

  return (
    <div data-testid="expenses-page">
      <PageHeader
        title="Expenses"
        subtitle="Track project costs and overhead."
        module="Procurement · MM"
        ribbon={ribbon}
      />
      <ExpenseUploadSheet projects={projects} onUploaded={load} open={uploadOpen} onOpenChange={setUploadOpen} hideTrigger />
      <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent className="w-[440px]">
              <SheetHeader><SheetTitle className="font-display">Add Expense</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-6">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Category">
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Amount"><Input type="number" data-testid="expense-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
                </div>
                <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
                <Field label="Project (optional)">
                  <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Vendor"><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></Field>
                <Field label="Description"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                <Button onClick={submit} data-testid="save-expense-button" className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">Save Expense</Button>
              </div>
            </SheetContent>
          </Sheet>

      <div className="p-6">
        {rows.length === 0 ? (
          <EmptyState title="No expenses" description="Log your first project or overhead expense." />
        ) : (
          <div className="bc-card overflow-hidden" data-testid="expenses-table">
            <Table className="bc-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e) => (
                  <React.Fragment key={e.id}>
                    <TableRow>
                      <TableCell className="text-[#605E5C]">{fmtDate(e.date)}</TableCell>
                      <TableCell><span className="text-[11px] font-semibold text-[#323130]">{e.category}</span></TableCell>
                      <TableCell>{projMap[e.project_id] || "—"}</TableCell>
                      <TableCell className="text-[#605E5C]">{e.vendor || "—"}</TableCell>
                      <TableCell className="text-[#605E5C] max-w-xs truncate">{e.description || "—"}</TableCell>
                      <TableCell className="text-right font-mono-num font-semibold text-[#A4262C]">{fmtMoney(e.amount)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} data-testid={`expand-expense-${e.id}`} title="Attachments" className="text-slate-500 hover:text-[#2E75B6] p-1"><Paperclip size={14} /></button>
                          <button onClick={() => del(e.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded === e.id && (
                      <TableRow className="bg-[#FAF9F8]">
                        <TableCell colSpan={7} className="p-4">
                          <div className="max-w-md">
                            <Attachments parentType="expense" parentId={e.id} />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
